import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { apiAuth } from "@/lib/api";

const EVENT_GROUPS = [
  "startup_submitted", "investor_submitted", "ai_analysis_completed",
  "ai_analysis_failed", "pitch_deck_uploaded", "pitch_deck_downloaded",
  "startup_status_changed", "investor_status_changed",
  "note_created", "note_updated", "note_deleted",
  "tag_added", "tag_removed",
  "admin_login_success", "admin_login_failed", "admin_logout",
  "password_changed", "password_reset_requested", "password_reset_completed",
  "newsletter_subscribed", "contact_submission_received",
];

export default function AdminAuditLog() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const limit = Number(params.get("limit") || 50);
  const eventType = params.get("event_type") || "";
  const entityType = params.get("entity_type") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const query = useMemo(() => {
    const s = new URLSearchParams();
    s.set("page", String(page));
    s.set("limit", String(limit));
    if (eventType) s.set("event_type", eventType);
    if (entityType) s.set("entity_type", entityType);
    return s.toString();
  }, [page, limit, eventType, entityType]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiAuth.get(`/admin/audit?${query}`).then(({ data }) => {
      if (!cancelled) { setData(data); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const updateParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null) next.delete(k); else next.set(k, String(v));
    next.set("page", "1");
    setParams(next);
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="flex flex-col gap-5" data-testid="page-admin-audit">
      <div className="bg-white border border-sandBeige rounded-3xl p-4 grid sm:grid-cols-3 gap-3">
        <select
          className="wit-input"
          value={eventType}
          onChange={(e) => updateParam("event_type", e.target.value)}
          data-testid="filter-event-type"
        >
          <option value="">Alle Event-Types</option>
          {EVENT_GROUPS.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select
          className="wit-input"
          value={entityType}
          onChange={(e) => updateParam("entity_type", e.target.value)}
          data-testid="filter-entity-type"
        >
          <option value="">Alle Entitäten</option>
          {["startup", "investor", "user", "contact", "pitch_deck", "ai_analysis", "newsletter", "contact_submission", "investor_consent"].map((e) =>
            <option key={e} value={e}>{e}</option>
          )}
        </select>
      </div>

      <div className="bg-white border border-sandBeige rounded-3xl divide-y divide-sandBeige/60 overflow-hidden">
        {loading && <p className="px-5 py-10 text-center text-warmGrey">Lade …</p>}
        {!loading && data?.items?.length === 0 && <p className="px-5 py-10 text-center text-warmGrey">Keine Einträge.</p>}
        {data?.items?.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => setSelected(e)}
            className="w-full text-left px-5 py-3.5 hover:bg-ivory/60 transition-colors flex items-start gap-4"
            data-testid={`audit-row-${e.id}`}
          >
            <span className="text-xs text-warmGrey w-44 shrink-0">{new Date(e.timestamp).toLocaleString("de-DE")}</span>
            <span className="text-xs bg-porcelain border border-sandBeige rounded-full px-2 py-0.5 text-cocoa">{e.event_type}</span>
            <span className="text-xs text-warmGrey hidden sm:inline">{e.entity_type}</span>
            <span className="text-xs text-cocoa font-mono truncate flex-1">{e.entity_id || "—"}</span>
            <span className="text-xs text-warmGrey hidden md:inline">{e.actor_type || "system"}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between text-sm text-warmGrey">
        <span>{total} Einträge</span>
        <div className="flex items-center gap-3">
          <button type="button" disabled={page <= 1} onClick={() => updateParam("page", page - 1)} className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40">‹ Prev</button>
          <span className="text-cocoa">{page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => updateParam("page", page + 1)} className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40">Next ›</button>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-cocoa/40 flex items-center justify-center z-50 p-5" onClick={() => setSelected(null)}>
          <div className="bg-white border border-sandBeige rounded-3xl p-6 max-w-lg w-full shadow-soft" onClick={(e) => e.stopPropagation()}>
            <p className="font-serif text-xl text-cocoa">{selected.event_type}</p>
            <p className="text-xs text-warmGrey mt-1">{new Date(selected.timestamp).toLocaleString("de-DE")}</p>
            <hr className="my-4 border-sandBeige" />
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-3"><dt className="text-warmGrey">Entity Type</dt><dd className="text-cocoa">{selected.entity_type}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmGrey">Entity ID</dt><dd className="text-cocoa font-mono text-xs break-all">{selected.entity_id}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-warmGrey">Actor</dt><dd className="text-cocoa">{selected.actor_type} / {selected.actor_id || "—"}</dd></div>
              <div><dt className="text-warmGrey mb-1">Metadata</dt><dd className="bg-porcelain rounded-2xl p-3 text-xs font-mono whitespace-pre-wrap break-all">{JSON.stringify(selected.metadata || {}, null, 2)}</dd></div>
            </dl>
            <button type="button" onClick={() => setSelected(null)} className="wit-btn-secondary mt-5 w-full">Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}
